import React, { useState } from 'react';
import { Edit3 } from 'lucide-react';
import { KeyResult, KeyResultType } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { KPIProgressUpdate } from './KPIProgressUpdate';
import { KPIProgressValue } from '@/features/goals/shared/KPIProgressValue';
import { CircularProgress } from '@/features/goals/shared/CircularProgress';
import {
  type FullIssueWithChildren,
  canEditIssue,
} from '@/shared/utils/issue-guards';

interface GoalKPIsProps {
  goalId: string;
  keyResults?: KeyResult[];
  status: string;
  onKPIClick?: () => void;
  currentEmployeeId?: string;
  goal?: FullIssueWithChildren;
}

export const GoalKPIs: React.FC<GoalKPIsProps> = ({
  goalId,
  keyResults,
  onKPIClick,
  goal,
}) => {
  const [openPopover, setOpenPopover] = useState<number | null>(null);

  if (!keyResults || keyResults.length === 0) {
    return null;
  }

  // Separate KPIs by type
  // Fallback to MAXIMUM for existing KPIs without type field
  const maximumKPIs = keyResults.filter(
    (kr) => (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MAXIMUM
  );
  const minimumKPIs = keyResults.filter(
    (kr) => (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MINIMUM
  );
  const canEdit = goal ? canEditIssue(goal) : false;

  const renderKPI = (kr: KeyResult, index: number) => {
    const originalIndex = keyResults.indexOf(kr);
    const isMinimum =
      (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MINIMUM;

    // For minimum KPIs (like complaints), reverse the progress calculation
    // If you have 7 complaints out of 10, that's 30% progress (3 remaining)
    // Guard against division by zero and clamp to 0-100 range
    const progress =
      kr.quantity > 0
        ? Math.min(
            Math.max(
              isMinimum
                ? ((kr.quantity - kr.currentProgress) / kr.quantity) * 100
                : (kr.currentProgress / kr.quantity) * 100,
              0
            ),
            100
          )
        : 0;

    return (
      <div key={index} className="space-y-1.5 group/kpi">
        {/* Circular Progress and Title/Value */}
        <div
          className="flex items-center gap-6 cursor-pointer"
          onClick={onKPIClick}
        >
          <CircularProgress
            progress={progress}
            startDate={goal?.startDate}
            dueDate={goal?.dueDate}
            size={48}
            strokeWidth={5}
            isMinimum={isMinimum}
          />
          {/* Title and Value stacked */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">
                {kr.title}
              </span>
              {canEdit && (
                <div onClick={(e) => e.stopPropagation()}>
                  <KPIProgressUpdate
                    goalId={goalId}
                    keyResult={kr}
                    keyResultIndex={originalIndex}
                    allKeyResults={keyResults}
                    isOpen={openPopover === originalIndex}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? originalIndex : null)
                    }
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover/kpi:opacity-100 transition-opacity"
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  </KPIProgressUpdate>
                </div>
              )}
            </div>
            <KPIProgressValue
              currentProgress={kr.currentProgress}
              quantity={kr.quantity}
              unit={kr.unit}
              variant="inline"
            />
          </div>

          {/* Circular Progress Indicator */}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 ms-9 space-y-3">
      {/* All KPIs - Progress bars */}
      {maximumKPIs.map((kr, index) => renderKPI(kr, index))}
      {minimumKPIs.map((kr, index) => renderKPI(kr, index))}
    </div>
  );
};
